// ============================================================
// ELC Shift — Uploads / Stores module (v104.0)
//
// Store CRUD + opening hours management. Distinct from
// ALL_STORES (the global cache in index.html which stays).
//
// Public surface:
//   • loadStoresFromDB
//   • switchStoreTab
//   • renderStoreHours
//   • openStoreHoursModal
//   • closeStoreHoursModal
//   • saveStoreHoursFromModal
//   • saveStoreHour
//   • saveStoreMeta
//   • renderStoreManagement
//   • closeStoreModal
//   • saveStoreModal
//   • deleteStore
//
// Free globals consumed:
//   DB, showToast, escapeHtml, formatDateShort, ALL_STORES
// ============================================================

import { parseCoords } from '../shared/store-coords.js';   // v131.72

export async function loadStoresFromDB() {
  try {
    const rows = await DB.stores.list();
    if (rows && rows.length) {
      // The endpoint returns full store objects; the rest of the admin code
      // expects a flat string array of names. Preserve "Retail Relief Managers"
      // as a synthetic option since it isn't a real store row in the DB.
      const dbStores = rows.map(r => r.name).filter(Boolean);
      // v131.37: password state per store for the Stores table ("Set 11 Sep by X").
      window._storePasswordMeta = {};
      // v131.64 — and the store's Google review link, so the edit modal can
      // show what is already set. The phone field (v129.39) never did this and
      // therefore always opens blank; this one should not repeat that.
      window._storeGoogleMap = {};
      // v131.72 - the captured shop coordinate, same reason.
      window._storeCoordMap = {};
      rows.forEach(function (r) {
        if (!r || !r.name) return;
        window._storePasswordMeta[r.name] = { set: !!r.password_set, at: r.password_reset_at || null, by: r.password_reset_by || null };
        if (r.google_review_url) window._storeGoogleMap[r.name] = r.google_review_url;
        if (r.lat != null && r.lng != null && r.lat !== '' && r.lng !== '') window._storeCoordMap[r.name] = { lat: Number(r.lat), lng: Number(r.lng) };
      });
      const synthetic = ['Retail Relief Managers'];
      ALL_STORES = [...new Set([...dbStores, ...synthetic])];
      // v131.22 (Matt's new-store audit, 7 Sep): the rows carry area_id /
      // country / type / upload_name, but until now only the NAMES survived —
      // every meta reader (getStoreArea/Country/Type/UploadName,
      // buildUploadNameMap, AREA_STORE_MAP) fell back to THIS browser's
      // localStorage, so a store added on one machine had no area, a guessed
      // country, and a fallback upload name on every other admin's browser.
      // The DB is the authority: refresh the local cache from it on every
      // boot. DB rows overwrite per-store; entries the DB doesn't know
      // (synthetic/virtual-only) survive the merge. Nothing is lost by
      // trusting the DB — saveStoreMeta PATCHes it on every edit.
      try {
        let meta = {};
        try { meta = JSON.parse(localStorage.getItem('db_store_meta') || '{}') || {}; } catch (e) { meta = {}; }
        rows.forEach(function (r) {
          if (!r || !r.name) return;
          meta[r.name] = {
            area: r.area_id != null ? String(r.area_id) : '',
            country: r.country || 'ELC - UAE',
            type: r.type || 'physical',
            uploadName: r.upload_name || '',
          };
        });
        localStorage.setItem('db_store_meta', JSON.stringify(meta));
        localStorage.setItem('elc_store_meta', JSON.stringify(meta));
      } catch (e) { /* cache refresh is best-effort — readers keep their fallbacks */ }
      dlog('[DB] Loaded ' + dbStores.length + ' stores from database');
    } else {
      dlog('[DB] Stores list empty — keeping fallback');
    }
  } catch(e) {
    console.warn('[DB] Stores load failed, keeping hardcoded fallback:', e.message);
  }
}

export function switchStoreTab(tab) {
  document.getElementById('storeListPanel').style.display = tab === 'list' ? 'block' : 'none';
  document.getElementById('storeHoursPanel').style.display = tab === 'hours' ? 'block' : 'none';
  document.getElementById('storeTabList').className = tab === 'list' ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('storeTabHours').className = tab === 'hours' ? 'btn btn-green' : 'btn btn-outline';
  // v129.42 — 📇 Contacts. Guarded on the elements existing so an older cached
  // admin.html against a new bundle degrades to the two tabs it knows, rather
  // than throwing here and leaving every store panel hidden.
  var cPanel = document.getElementById('storeContactsPanel');
  if (cPanel) cPanel.style.display = tab === 'contacts' ? 'block' : 'none';
  var cTab = document.getElementById('storeTabContacts');
  if (cTab) cTab.className = tab === 'contacts' ? 'btn btn-green' : 'btn btn-outline';
  if (tab === 'hours') renderStoreHours();
  if (tab === 'contacts' && typeof ContactsAdmin !== 'undefined' && ContactsAdmin.render) ContactsAdmin.render();
}

export async function renderStoreHours() {
  const tbody = document.getElementById('storeHoursBody');
  if (!tbody) return;

  tbody.innerHTML = '<tr><td colspan="4" style="padding:20px;text-align:center;color:var(--sub);font-size:12px">Loading hours…</td></tr>';

  try {
    // Pull all rows in one shot (cached). Filter per-season in-memory below.
    _hoursData = await DB.openingHours.list();
  } catch (e) {
    console.warn('[hours] load failed:', e && e.message);
    tbody.innerHTML = '<tr><td colspan="4" style="padding:20px;text-align:center;color:var(--red);font-size:12px">Failed to load hours: '+(e && e.message)+'</td></tr>';
    return;
  }

  // Refresh the active-season indicator from app_config in parallel.
  // Non-blocking — if the read fails the indicator stays on its last value.
  loadCurrentSeason();

  // Build a fast lookup: hoursByStore[store][season] = row
  var hoursByStore = {};
  (_hoursData || []).forEach(function(row){
    if (!hoursByStore[row.store]) hoursByStore[row.store] = {};
    hoursByStore[row.store][row.season] = row;
  });

  var stores = (ALL_STORES || []).filter(function(s){ return s !== 'Retail Relief Managers'; });
  if (stores.length === 0) {
    tbody.innerHTML = '<tr><td colspan="4" style="padding:20px;text-align:center;color:var(--sub);font-size:12px">No stores loaded yet.</td></tr>';
    return;
  }

  tbody.innerHTML = stores.map(function(store){
    var isQatar = store.indexOf('Doha') === 0;
    var flag = isQatar ? '🇶🇦' : '🇦🇪';
    var country = isQatar ? 'Qatar' : 'UAE';
    var row = hoursByStore[store] && hoursByStore[store][_hoursEditingSeason];
    var summary = row ? summarizeHoursRow(row) : '<span style="color:var(--red)">No '+_hoursEditingSeason+' hours set</span>';
    return '<tr>'
      + '<td><strong translate="no">'+escapeHtml(store)+'</strong></td>'
      + '<td style="font-size:11px;white-space:nowrap">'+flag+' '+country+'</td>'
      + '<td style="font-size:11px;color:var(--sub)">'+summary+'</td>'
      + '<td style="text-align:end"><button class="btn btn-outline btn-sm" onclick="Uploads.stores.openStoreHoursModal('+JSON.stringify(store).replace(/"/g,'&quot;')+')">Edit</button></td>'
      + '</tr>';
  }).join('');
}

export function openStoreHoursModal(store) {
  _hoursModalStore = store;
  _hoursModalSeason = _hoursEditingSeason;

  // Find the existing row (if any) for this store + season
  var row = (_hoursData || []).find(function(r){
    return r.store === store && r.season === _hoursEditingSeason;
  }) || {};

  document.getElementById('storeHoursModalStore').textContent = store;
  document.getElementById('storeHoursModalSeason').textContent = _hoursEditingSeason.charAt(0).toUpperCase() + _hoursEditingSeason.slice(1);

  // Build the 7-row grid
  var grid = document.getElementById('storeHoursModalGrid');
  if (!grid) return;   // v136.8: null-safe (the box may not be on screen)
  grid.innerHTML = HOURS_DAYS.map(function(d){
    var open  = row[d.key+'_open']  || '';
    var close = row[d.key+'_close'] || '';
    return '<div style="display:grid;grid-template-columns:90px 1fr 1fr;gap:8px;align-items:center;margin-bottom:8px">'
      +   '<div style="font-size:12px;font-weight:600">'+d.label+'</div>'
      +   '<input type="time" id="hoursInput_'+d.key+'_open"  value="'+escapeAttr(safeTimeInputValue(open))+'"  style="border:1px solid var(--border);border-radius:6px;padding:6px;font-size:12px;font-family:inherit">'
      +   '<input type="time" id="hoursInput_'+d.key+'_close" value="'+escapeAttr(safeTimeInputValue(close))+'" style="border:1px solid var(--border);border-radius:6px;padding:6px;font-size:12px;font-family:inherit">'
      + '</div>';
  }).join('');

  // Column headers on top of the inputs
  grid.insertAdjacentHTML('afterbegin',
    '<div style="display:grid;grid-template-columns:90px 1fr 1fr;gap:8px;margin-bottom:6px;font-size:11px;color:var(--sub);text-transform:uppercase;letter-spacing:0.4px;font-weight:600">'
    + '<div></div><div>Open</div><div>Close</div></div>'
  );

  document.getElementById('storeHoursModal').style.display = 'flex';
}

export function closeStoreHoursModal() {
  document.getElementById('storeHoursModal').style.display = 'none';
  _hoursModalStore = null;
  _hoursModalSeason = null;
}

export async function saveStoreHoursFromModal() {
  if (!_hoursModalStore || !_hoursModalSeason) return;

  // Build the row payload — collect each day's two inputs.
  var payload = { store: _hoursModalStore, season: _hoursModalSeason };
  HOURS_DAYS.forEach(function(d){
    var openEl  = document.getElementById('hoursInput_'+d.key+'_open');
    var closeEl = document.getElementById('hoursInput_'+d.key+'_close');
    payload[d.key+'_open']  = (openEl  && openEl.value)  ? openEl.value  : null;
    payload[d.key+'_close'] = (closeEl && closeEl.value) ? closeEl.value : null;
  });

  try {
    await DB.openingHours.save(payload);
    // Refresh the cache and table — the local cache won't have the new
    // values otherwise. Don't refetch from network for the optimistic
    // case; update _hoursData inline.
    var existing = (_hoursData || []).findIndex(function(r){
      return r.store === payload.store && r.season === payload.season;
    });
    if (existing >= 0) {
      _hoursData[existing] = Object.assign({}, _hoursData[existing], payload);
    } else {
      _hoursData.push(payload);
    }
    showToast('Hours saved for '+payload.store, 'success');
    closeStoreHoursModal();
    renderStoreHours();
  } catch (e) {
    showToast('Save failed: '+(e && e.message), 'error');
  }
}

export function saveStoreHour(store, field, value) {
  // v82.0 — legacy function kept as a no-op for any stale call sites.
  // The old weekday/weekend localStorage flow was replaced by the
  // openStoreHoursModal → saveStoreHoursFromModal path. If anything
  // still calls this (it shouldn't — the old inputs were removed),
  // we surface a console warning rather than silently corrupting
  // localStorage.
  console.warn('[hours] saveStoreHour() called — this is legacy and was removed in v82.0. Use openStoreHoursModal() instead.');
}

export function saveStoreMeta(meta) {
  localStorage.setItem('db_store_meta', JSON.stringify(meta));
  localStorage.setItem('elc_store_meta', JSON.stringify(meta));
  // Persist per-store updates to DB.
  //
  // IMPORTANT: the local meta object uses camelCase keys (area, uploadName)
  // because that's what the form fields and downstream accessors expect.
  // The `stores` table schema uses snake_case (area_id, upload_name), so
  // we MUST translate at this boundary — otherwise PostgREST returns
  // PGRST204 ("Could not find the 'area' column of 'stores'") and the
  // save fails with a 500. (Also: empty-string area means "None / Virtual"
  // which we send as NULL so the FK-style column doesn't carry a junk value.)
  Object.entries(meta).forEach(([name, data]) => {
    const dbPayload = {
      area_id:     data.area || null,
      country:     data.country,
      type:        data.type,
      upload_name: data.uploadName || null,
    };
    DB.stores.update(name, dbPayload).catch(e => {
      console.error('[DB] Store meta save failed:', e.message);
      showToast('⚠️ Store settings save failed in DB', 'error');
    });
  });
}

// v125.53 — store → email-address map, kept in sync so store setup can show and
// edit each store's inbox. A plain admin fetch (Bearer); cached on window so the
// (synchronous) edit-modal opener can read it. Best-effort — a missing map just
// means blank email fields, never a broken store screen.
function _storeEmailApi(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
export async function loadStoreEmailMap() {
  try {
    var res = await _storeEmailApi('/api/email/map');
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) { window._storeEmailMap = d.map || {}; renderStoreManagement(); }
  } catch (e) { /* best-effort */ }
}
// Persist a store's address to store_email_map (real + letterbox), or clear it.
// Returns the fetch promise so callers can surface a failure.
// v129.39 — the store landline. Goes to the `stores` row itself (via the
// existing admin CRUD), NOT to the localStorage store-meta blob beside it:
// every till reads this number off the API for the 📇 Contacts screen, and a
// per-browser copy would show 28 different answers.
export function saveStorePhone(store, phone) {
  return _storeEmailApi('/admin/stores?name=' + encodeURIComponent(store), {
    method: 'PATCH', body: JSON.stringify({ phone: phone || null }),
  });
}

// v131.64 — the store's Google review link (Matt: "we are low on google
// reviews"). Same route as the landline; the column is on the `stores` row so
// the Worker can hand it to the survey's thank-you screen for that one store.
export function saveStoreGoogleUrl(store, url) {
  return _storeEmailApi('/admin/stores?name=' + encodeURIComponent(store), {
    method: 'PATCH', body: JSON.stringify({ google_review_url: url || null }),
  });
}

// v131.72 - the shop's coordinate. null clears it (a store with no coordinate
// is simply never matched by the survey's Find my store, which is the correct
// behaviour - it is never guessed at).
export function saveStoreCoords(store, coords) {
  return _storeEmailApi('/admin/stores?name=' + encodeURIComponent(store), {
    method: 'PATCH',
    body: JSON.stringify(coords
      ? { lat: coords.lat, lng: coords.lng, location_captured_at: new Date().toISOString() }
      : { lat: null, lng: null, location_captured_at: null }),
  });
}

export function saveStoreEmail(store, email) {
  window._storeEmailMap = window._storeEmailMap || {};
  if (email) window._storeEmailMap[store] = email; else delete window._storeEmailMap[store];
  return _storeEmailApi('/api/email/map', { method: 'POST', body: JSON.stringify({ store: store, email: email || '' }) });
}

// v131.37: "when was this store's password last set, and by whom".
export function storePasswordCell(name) {
  const pw = (window._storePasswordMeta && window._storePasswordMeta[name]) || null;
  const esc = (s) => String(s == null ? '' : s).replace(/</g, '&lt;');
  if (!pw || !pw.set) return '<span style="color:var(--red);font-weight:600">No password</span>';
  if (!pw.at) return '<span style="color:var(--sub)">Set (before 11 Sep 2026)</span>';
  let when = pw.at;
  try { when = new Date(pw.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { /* raw */ }
  return '<strong>Set ' + esc(when) + '</strong>' + (pw.by ? '<div style="color:var(--sub)">by <span translate="no">' + esc(pw.by) + '</span></div>' : '');
}

// v131.41 - the bulk rotate (Matt's post-incident ask). Head Office only,
// typed confirmation, the plaintexts are shown ONCE and never stored.
export async function rotateAllStorePasswords() {
  const typed = prompt('This gives EVERY active store a new random device password and shows them ONCE.\n\nEvery till re-prompts on its next load. Tell each store by phone or chat, never by email.\n\nType ROTATE to continue.');
  if (typed !== 'ROTATE') return;
  let res;
  try { res = await DB.auth.rotateAllStorePasswords(); }
  catch (e) { showToast('Rotate failed: ' + ((e && e.message) || 'unknown'), 'error'); return; }
  const rows = (res && Array.isArray(res.rotated)) ? res.rotated : [];
  const failed = (res && Array.isArray(res.failed)) ? res.failed : [];
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const text = rows.map((r) => r.store + '\t' + r.password).join('\n');
  const ov = document.createElement('div');
  ov.id = 'storeRotateResult';
  ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px';
  ov.innerHTML = '<div style="background:var(--card);border-radius:14px;max-width:560px;width:100%;max-height:90vh;overflow:auto;padding:18px;box-shadow:0 20px 60px rgba(0,0,0,.3)">' +
    '<h3 style="margin:0 0 6px">New store passwords (' + rows.length + ')</h3>' +
    '<div style="font-size:12px;color:var(--adm-red-text);font-weight:600;margin-bottom:10px">Shown once. Closing this loses the list - copy it first.</div>' +
    (failed.length ? '<div style="font-size:12px;color:var(--adm-amber-text);margin-bottom:8px">Not rotated: <span translate="no">' + esc(failed.join(', ')) + '</span></div>' : '') +
    '<table class="data-table" style="width:100%"><thead><tr><th>Store</th><th>New password</th></tr></thead><tbody>' +
    rows.map((r) => '<tr><td translate="no">' + esc(r.store) + '</td><td style="font-family:monospace;font-size:14px" translate="no">' + esc(r.password) + '</td></tr>').join('') +
    '</tbody></table>' +
    '<div style="display:flex;gap:8px;margin-top:12px;justify-content:flex-end">' +
    '<button class="btn btn-outline" id="storeRotateCopy">Copy all</button>' +
    '<button class="btn btn-green" id="storeRotateClose">Close</button></div></div>';
  document.body.appendChild(ov);
  ov.querySelector('#storeRotateCopy').onclick = function () {
    try { navigator.clipboard.writeText(text).then(function () { showToast('Copied', 'success'); }); }
    catch (e) { showToast('Copy failed - select the table and copy by hand', 'error'); }
  };
  ov.querySelector('#storeRotateClose').onclick = function () {
    if (confirm('Close and lose the list? Make sure you copied it.')) ov.remove();
  };
  try { await loadStoresFromDB(); renderStoreManagement(); } catch (e) { /* cosmetic */ }
}

export function renderStoreManagement() {
  const tbody = document.getElementById('storeManagementBody');
  if (!tbody) return;
  // v131.41: the bulk rotate is Head Office only.
  // v136.9: so is the per-store 🔓 (end a store password lockout early); the Worker refuses anyone else.
  let rbIsHo = false;
  try { var rbWho = (typeof currentAdmin !== 'undefined') ? currentAdmin : null; rbIsHo = !!(rbWho && String(rbWho.role || '').toLowerCase() === 'admin'); } catch (e) { rbIsHo = false; }
  try {
    const rb = document.getElementById('storeRotateAllBtn');
    // v131.47: admin.html declares currentAdmin with let - it is a bare global for classic-script bundles, NOT a window property (Matt could not see the button on prod v131.45).
    var rbAdmin = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
    if (rb) rb.style.display = (rbAdmin && String(rbAdmin.role || '').toLowerCase() === 'admin') ? '' : 'none';
  } catch (e) { /* cosmetic */ }
  if (window._storeEmailMap === undefined) { window._storeEmailMap = null; loadStoreEmailMap(); }

  // Build store list: ALL_STORES + any extras from staffData
  const storeSet = new Set(ALL_STORES);
  staffData.forEach(s => { if (s.store) storeSet.add(s.store); });
  const stores = [...storeSet].sort();

  tbody.innerHTML = stores.map(name => {
    const count = staffData.filter(s => s.store === name && s.active !== false).length;
    const area = getStoreArea(name) || '—';
    const country = getStoreCountry(name).replace('ELC - ', '');
    const type = getStoreType(name);
    const uploadName = getStoreUploadName(name);
    const uploadSame = uploadName === name;
    const typeBadge = type === 'virtual'
      ? '<span style="background:var(--adm-purple-tint);color:var(--adm-purple-text);padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600">Virtual</span>'
      : '<span style="background:var(--ok-bg);color:var(--ok-deep);padding:2px 7px;border-radius:5px;font-size:10px;font-weight:600">Physical</span>';
    const canDelete = count === 0;
    const email = (window._storeEmailMap && window._storeEmailMap[name]) || '';
    const emailLine = email
      ? `<div style="font-size:11px;color:var(--sub)" translate="no">📧 ${email.replace(/</g,'&lt;')}</div>`
      : '';
    return `<tr>
      <td><strong translate="no">${name}</strong>${emailLine}</td>
      <td>${country}</td>
      <td>${area}</td>
      <td style="font-size:11px;color:${uploadSame?'var(--sub)':'var(--text)'}">
        ${uploadSame ? '<span style="color:var(--sub);font-style:italic">same as store name</span>' : `<strong translate="no">${uploadName}</strong>`}
      </td>
      <td>${count} staff</td>
      <td>${typeBadge}</td>
      <td style="font-size:11px">${storePasswordCell(name)}</td>
      <td style="text-align:end;white-space:nowrap">
        <button class="btn btn-outline btn-sm" onclick="openEditStoreModal('${name.replace(/'/g,"\\'")}')">Edit</button>
        <button class="btn btn-outline btn-sm" onclick="changeStorePassword('${name.replace(/'/g,"\\'")}')">🔑</button>
        ${rbIsHo ? `<button class="btn btn-outline btn-sm" title="Unlock sign-in after too many wrong passwords" data-store="${String(name).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;')}" onclick="unlockStoreLogin(this.dataset.store)">🔓</button>` : ''}
        ${canDelete ? `<button class="btn btn-outline btn-sm" style="color:var(--red);border-color:var(--red);margin-inline-start:4px" onclick="Uploads.stores.deleteStore('${name.replace(/'/g,"\\'")}')">Delete</button>` : ''}
      </td>
    </tr>`;
  }).join('');
}

export function closeStoreModal() {
  // v131.21 — cancelling the modal abandons any pending Amanah code link.
  window._pendingStoreCodeLink = null;
  document.getElementById('storeModal').classList.remove('open');
}

export async function saveStoreModal() {
  const newName    = document.getElementById('smStoreName').value.trim();
  const uploadName = document.getElementById('smUploadName').value.trim();
  const area       = document.getElementById('smStoreArea').value;
  const country    = document.getElementById('smStoreCountry').value;
  const type       = document.getElementById('smStoreType').value;
  const oldName    = document.getElementById('smStoreOldName').value;

  if (!newName) { showToast('Enter a store name', 'error'); return; }

  // v131.21 (found while building the Al Reem onboarding flow): a NEW store
  // never reached the database. saveStoreMeta below only PATCHes by name, and
  // PostgREST PATCH on a name that matches no row succeeds by writing NOTHING
  // — so "Add Store" existed in this browser's localStorage and nowhere else
  // (no till picker entry, no staff assignment target). Create the row first,
  // and fail LOUDLY: without it nothing downstream works.
  if (!oldName) {
    try {
      await DB.stores.create({
        name: newName,
        area_id: area || null,
        country: country,
        type: type,
        upload_name: uploadName || null,
        active: true,
      });
    } catch (e) {
      const msg = String((e && e.message) || e);
      // An already-existing row (someone re-adds a known store) degrades to an
      // edit — everything after this point PATCHes it.
      if (!/duplicate|already exists|23505|409/i.test(msg)) {
        showToast('Store could NOT be created: ' + msg, 'error');
        return;   // leave the modal open — nothing was saved
      }
    }
  }

  const meta = getStoreMeta();

  if (oldName && oldName !== newName) {
    staffData.forEach(s => { if (s.store === oldName) s.store = newName; });
    if (meta[oldName]) { meta[newName] = meta[oldName]; delete meta[oldName]; }
    for (const a of Object.keys(AREA_STORE_MAP)) {
      AREA_STORE_MAP[a] = AREA_STORE_MAP[a].map(s => s === oldName ? newName : s);
    }
    showToast(`Renamed "${oldName}" → "${newName}"`, 'success');
  } else if (!oldName) {
    showToast(`Store "${newName}" added`, 'success');
  }

  // Save metadata — uploadName blank means "same as store name"
  meta[newName] = { area, country, type, uploadName: uploadName || '' };
  saveStoreMeta(meta);

  if (type === 'physical' && area && !AREA_STORE_MAP[area]?.includes(newName)) {
    if (AREA_STORE_MAP[area]) AREA_STORE_MAP[area].push(newName);
  }

  // v125.53 — persist the store's email to store_email_map (real + letterbox), so
  // "add a store" also sets up its inbox. Only fires when the address changed
  // (or the store was renamed while holding an address), so we don't hammer the
  // endpoint on every unrelated edit. A rename re-points the existing rows by
  // POSTing under the new name (the addresses are the UNIQUE key).
  // v129.39 — persist the landline. Same "only when it changed" rule as the
  // email below, so an unrelated edit does not PATCH every store.
  const phoneEl = document.getElementById('smStorePhone');
  if (phoneEl) {
    const phone = String(phoneEl.value || '').trim();
    const prevPhone = (window._storePhoneMap && window._storePhoneMap[newName]) || '';
    if (phone !== prevPhone) {
      window._storePhoneMap = window._storePhoneMap || {};
      if (phone) window._storePhoneMap[newName] = phone; else delete window._storePhoneMap[newName];
      saveStorePhone(newName, phone)
        .then(r => r.json().catch(() => ({})))
        .then(d => { if (d && d.ok === false) showToast('Store saved — phone not set: ' + (d.error || 'try again'), 'error'); })
        .catch(() => showToast('Store saved, but the phone number could not be saved', 'error'));
    }
  }

  // v131.64 — the store's own Google review link, offered on the customer
  // survey's thank-you screen. Same "only when it changed" rule as the phone
  // above. The Worker validates it before any customer sees it (https on a
  // Google host); an admin-set link rendered as a button on a public page is
  // an open redirect otherwise.
  const gEl = document.getElementById('smStoreGoogleUrl');
  if (gEl) {
    const gUrl = String(gEl.value || '').trim();
    const prevG = (window._storeGoogleMap && window._storeGoogleMap[newName]) || '';
    if (gUrl !== prevG) {
      window._storeGoogleMap = window._storeGoogleMap || {};
      if (gUrl) window._storeGoogleMap[newName] = gUrl; else delete window._storeGoogleMap[newName];
      saveStoreGoogleUrl(newName, gUrl)
        .then(r => r.json().catch(() => ({})))
        .then(d => { if (d && d.ok === false) showToast('Store saved — Google link not set: ' + (d.error || 'try again'), 'error'); })
        .catch(() => showToast('Store saved, but the Google review link could not be saved', 'error'));
    }
  }

  // v131.72 - the shop's coordinate. Same "only when it changed" rule. An
  // unparseable box is a LOUD failure, not a silent clear: somebody who typed
  // the numbers wrong must be told, or the store quietly stops being matched.
  const cEl = document.getElementById('smStoreCoords');
  if (cEl) {
    const raw = String(cEl.value || '').trim();
    const parsed = raw ? parseCoords(raw) : null;
    if (raw && !parsed) {
      showToast('Store saved, but the location was not understood - use "latitude, longitude", e.g. 25.118, 55.200', 'error');
    } else {
      const prevC = (window._storeCoordMap && window._storeCoordMap[newName]) || null;
      const prevStr = prevC ? (Number(prevC.lat).toFixed(6) + ', ' + Number(prevC.lng).toFixed(6)) : '';
      const nowStr = parsed ? (parsed.lat.toFixed(6) + ', ' + parsed.lng.toFixed(6)) : '';
      if (nowStr !== prevStr) {
        window._storeCoordMap = window._storeCoordMap || {};
        if (parsed) window._storeCoordMap[newName] = parsed; else delete window._storeCoordMap[newName];
        saveStoreCoords(newName, parsed)
          .then(r => r.json().catch(() => ({})))
          .then(d => { if (d && d.ok === false) showToast('Store saved - location not set: ' + (d.error || 'try again'), 'error'); })
          .catch(() => showToast('Store saved, but the location could not be saved', 'error'));
      }
    }
  }

  const emailEl = document.getElementById('smStoreEmail');
  const email = emailEl ? String(emailEl.value || '').trim() : '';
  const prevEmail = (window._storeEmailMap && (window._storeEmailMap[newName] || (oldName && window._storeEmailMap[oldName]))) || '';
  const renamedWithEmail = oldName && oldName !== newName && !!prevEmail;
  if (email !== prevEmail || renamedWithEmail) {
    if (oldName && oldName !== newName && window._storeEmailMap) delete window._storeEmailMap[oldName];
    saveStoreEmail(newName, email)
      .then(r => r.json().catch(() => ({})))
      .then(d => { if (d && d.ok === false) showToast('Store saved — email not set: ' + (d.error || 'try again'), 'error'); renderStoreManagement(); })
      .catch(() => showToast('Store saved, but the email address could not be saved', 'error'));
  }

  // v131.21 — the "new store spotted in the Amanah feed" flow: the review card
  // stashes the unlinked store code before opening this modal; a successful
  // save links it and refreshes the review so the waiting staff resolve.
  const pending = window._pendingStoreCodeLink;
  if (pending && pending.code && !oldName) {
    window._pendingStoreCodeLink = null;
    try {
      await DB.stores.linkCode(String(pending.code), newName);
      showToast('Amanah code ' + pending.code + ' linked to ' + newName, 'success');
      if (typeof window.loadStaffChanges === 'function') window.loadStaffChanges();
    } catch (e) {
      showToast('Store saved, but the code could not be linked: ' + ((e && e.message) || e), 'error');
    }
  }

  closeStoreModal();
  renderStoreManagement();
  populateStoreSelects();
}

export async function deleteStore(name) {
  const count = staffData.filter(s => s.store === name).length;
  if (count > 0) { showToast('Cannot delete — ' + count + ' staff assigned', 'error'); return; }
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete store "' + name + '"? This cannot be undone.', { danger: true, confirmLabel: 'Delete' }))) return;
  const meta = getStoreMeta();
  delete meta[name];
  saveStoreMeta(meta);
  for (const area of Object.keys(AREA_STORE_MAP)) {
    AREA_STORE_MAP[area] = AREA_STORE_MAP[area].filter(s => s !== name);
  }
  showToast('Store deleted', 'success');
  renderStoreManagement();
}

